import { useApolloClient } from "@apollo/client";
import {
  CollectionListDocument,
  CollectionListQuery,
  CollectionListQueryVariables,
  PromotionByNameDocument,
  PromotionByNameQuery,
  PromotionByNameQueryVariables,
  PromotionTypeEnum,
  RewardValueTypeEnum,
  useCollectionAssignProductMutation,
  useCreateCollectionMutation,
  usePromotionByNameQuery,
  usePromotionCreateMutation,
  usePromotionRuleCreateMutation,
  usePromotionRuleDeleteMutation,
  usePromotionRuleUpdateMutation,
  useUnassignCollectionProductMutation,
} from "@dashboard/graphql";
import { useState } from "react";

import {
  autoDiscountPromotionName,
  DesiredDiscount,
  ExistingRule,
  getOwnedRewardByChannel,
  planVariantReconcile,
  SALE_COLLECTION_NAME,
  SALE_COLLECTION_SLUG,
} from "./misc";

export interface VariantChannelDiscountInput {
  channelId: string;
  /** Current selling price for the channel. */
  price: string;
  /** Absolute discounted price. Empty string clears the discount. */
  discountPrice: string;
}

export interface SyncVariantDiscountsArgs {
  productId: string;
  variantId: string;
  channels: VariantChannelDiscountInput[];
}

type PromotionNode = NonNullable<PromotionByNameQuery["promotions"]>["edges"][number]["node"];

const extractVariantIds = (cataloguePredicate: unknown): string[] => {
  const predicate = cataloguePredicate as { variantPredicate?: { ids?: string[] } } | null;

  return predicate?.variantPredicate?.ids ?? [];
};

const toExistingRules = (promotion: PromotionNode | undefined): ExistingRule[] =>
  (promotion?.rules ?? []).map(rule => ({
    id: rule.id,
    channelIds: (rule.channels ?? []).map(channel => channel.id),
    variantIds: extractVariantIds(rule.cataloguePredicate),
    rewardValue: rule.rewardValue === null ? null : Number(rule.rewardValue),
    rewardValueType: rule.rewardValueType,
  }));

// Turns the raw per-channel inputs into the discounts we actually want to
// persist: only channels whose discounted price is a valid, strictly-positive
// reduction of the selling price.
const toDesiredDiscounts = (channels: VariantChannelDiscountInput[]): DesiredDiscount[] =>
  channels.reduce<DesiredDiscount[]>((acc, { channelId, price, discountPrice }) => {
    if (discountPrice === "" || price === "") {
      return acc;
    }

    const reward = Number(price) - Number(discountPrice);

    if (!Number.isFinite(reward) || reward <= 0) {
      return acc;
    }

    return [...acc, { channelId, rewardValue: String(reward) }];
  }, []);

const cataloguePredicateFor = (variantId: string) => ({
  variantPredicate: { ids: [variantId] },
});

export interface AutoDiscountSyncResult {
  errors: Array<{ code?: string; field?: string | null; message?: string | null }>;
}

/**
 * Reconciles a variant's product-page "discount price" edits into Saleor's
 * catalogue Promotions model: one auto-managed promotion per product, one FIXED
 * rule per (variant, channel), and membership of the shared "Sale" collection so
 * the storefront can list discounted products.
 */
export const useAutoDiscountSync = () => {
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();

  const [promotionCreate] = usePromotionCreateMutation();
  const [ruleCreate] = usePromotionRuleCreateMutation();
  const [ruleUpdate] = usePromotionRuleUpdateMutation();
  const [ruleDelete] = usePromotionRuleDeleteMutation();
  const [createCollection] = useCreateCollectionMutation();
  const [assignProduct] = useCollectionAssignProductMutation();
  const [unassignProduct] = useUnassignCollectionProductMutation();

  const ensureSaleCollection = async (): Promise<string | undefined> => {
    const { data } = await client.query<CollectionListQuery, CollectionListQueryVariables>({
      query: CollectionListDocument,
      variables: { first: 1, filter: { slugs: [SALE_COLLECTION_SLUG] } },
      fetchPolicy: "network-only",
    });
    const existing = data?.collections?.edges[0]?.node.id;

    if (existing) {
      return existing;
    }

    const { data: created } = await createCollection({
      variables: { input: { name: SALE_COLLECTION_NAME, slug: SALE_COLLECTION_SLUG } },
    });

    return created?.collectionCreate?.collection?.id;
  };

  const setCollectionMembership = async (productId: string, shouldBeMember: boolean) => {
    const collectionId = await ensureSaleCollection();

    if (!collectionId) {
      return;
    }

    if (shouldBeMember) {
      await assignProduct({
        variables: { collectionId, productIds: [productId], moves: [] },
      });
    } else {
      await unassignProduct({
        variables: { collectionId, productIds: [productId] },
      });
    }
  };

  const syncVariantDiscounts = async ({
    productId,
    variantId,
    channels,
  }: SyncVariantDiscountsArgs): Promise<AutoDiscountSyncResult> => {
    setLoading(true);

    const errors: AutoDiscountSyncResult["errors"] = [];

    try {
      const name = autoDiscountPromotionName(productId);
      const { data } = await client.query<PromotionByNameQuery, PromotionByNameQueryVariables>({
        query: PromotionByNameDocument,
        variables: { name },
        fetchPolicy: "network-only",
      });
      const promotion = data?.promotions?.edges[0]?.node;

      const desired = toDesiredDiscounts(channels);
      const plan = planVariantReconcile(variantId, desired, toExistingRules(promotion));

      // No promotion yet — create one carrying the desired rules inline, then
      // add the product to the Sale collection.
      if (!promotion) {
        if (desired.length === 0) {
          return { errors };
        }

        const { data: createData } = await promotionCreate({
          variables: {
            input: {
              name,
              type: PromotionTypeEnum.CATALOGUE,
              rules: desired.map(({ channelId, rewardValue }) => ({
                channels: [channelId],
                rewardValueType: RewardValueTypeEnum.FIXED,
                rewardValue,
                cataloguePredicate: cataloguePredicateFor(variantId),
              })),
            },
          },
        });

        errors.push(...(createData?.promotionCreate?.errors ?? []));
        await setCollectionMembership(productId, true);

        return { errors };
      }

      // Existing promotion — apply the minimal diff.
      for (const ruleId of plan.toDelete) {
        const { data: deleteData } = await ruleDelete({ variables: { id: ruleId } });

        errors.push(...(deleteData?.promotionRuleDelete?.errors ?? []));
      }

      for (const { ruleId, rewardValue } of plan.toUpdate) {
        const { data: updateData } = await ruleUpdate({
          variables: {
            id: ruleId,
            input: { rewardValueType: RewardValueTypeEnum.FIXED, rewardValue },
          },
        });

        errors.push(...(updateData?.promotionRuleUpdate?.errors ?? []));
      }

      for (const { channelId, rewardValue } of plan.toCreate) {
        const { data: ruleData } = await ruleCreate({
          variables: {
            input: {
              promotion: promotion.id,
              channels: [channelId],
              rewardValueType: RewardValueTypeEnum.FIXED,
              rewardValue,
              cataloguePredicate: cataloguePredicateFor(variantId),
            },
          },
        });

        errors.push(...(ruleData?.promotionRuleCreate?.errors ?? []));
      }

      const remainingRules =
        (promotion.rules?.length ?? 0) - plan.toDelete.length + plan.toCreate.length;

      await setCollectionMembership(productId, remainingRules > 0);

      return { errors };
    } finally {
      setLoading(false);
    }
  };

  return { syncVariantDiscounts, loading };
};

/**
 * Loads the currently-persisted discount amounts (price − discountPrice) for a
 * variant, keyed by channel id, so the product page can pre-fill its inputs.
 */
export const useVariantDiscountRewards = (productId: string, variantId: string) => {
  const { data, loading } = usePromotionByNameQuery({
    variables: { name: autoDiscountPromotionName(productId) },
    fetchPolicy: "cache-and-network",
    skip: !productId || !variantId,
  });

  const promotion = data?.promotions?.edges[0]?.node;
  const rewardByChannel = getOwnedRewardByChannel(toExistingRules(promotion), variantId);

  return { rewardByChannel, loading };
};
